// src/lib/effort.ts
//
// "Nivel de análisis" (thinking effort) expuesto al usuario.
//
// El valor efectivo y el catálogo viven en el backend (LLMConfiguration, en el
// admin de Django); este archivo solo define el contrato y una copia local que
// permite renderizar el selector aunque la API no responda.

export type EffortOption = {
  /** Identificador que entiende el backend: "low" | "high" | "max". */
  value: string;
  /** Etiqueta corta (badge): Low / High / Max. */
  label: string;
  /** Nombre legible: Rápido / Profundo / Máximo. */
  title: string;
  /** Una línea de resumen para el listado. */
  summary: string;
  /** Explicación completa de qué implica el nivel. */
  description: string;
};

export const EFFORT_FALLBACK_OPTIONS: EffortOption[] = [
  {
    value: 'low',
    label: 'Low',
    title: 'Rápido',
    summary: 'Respuestas ágiles para el día a día.',
    description:
      'La IA analiza lo esencial antes de responder. Ideal para preguntas directas, ' +
      'resúmenes y consultas simples. Es el nivel más rápido.',
  },
  {
    value: 'high',
    label: 'High',
    title: 'Profundo',
    summary: 'Análisis cuidadoso de casos complejos.',
    description:
      'La IA dedica más tiempo a razonar y contrastar alternativas. Recomendado ' +
      'para estrategias, análisis de documentos y preguntas con matices. Puede ' +
      'tardar más que Low.',
  },
  {
    value: 'max',
    label: 'Max',
    title: 'Máximo',
    summary: 'Máximo razonamiento disponible.',
    description:
      'Usa el nivel de razonamiento más alto que permita el modelo activo. Para ' +
      'investigaciones extensas, demandas y documentos exigentes. Es el nivel más ' +
      'lento y el que más consume.',
  },
];

/** Devuelve la opción del catálogo que corresponde a un valor, si existe. */
export function findEffortOption(
  options: EffortOption[],
  value: string | null | undefined,
): EffortOption | undefined {
  if (!value) return undefined;
  return options.find((option) => option.value === value);
}

/** Texto de ayuda fijo ("¿qué es el nivel de análisis?"). */
export const EFFORT_HELP_TEXT =
  'El nivel de análisis controla cuánto razona la IA antes de responder: ' +
  'un nivel mayor puede mejorar el análisis de casos complejos, pero tarda más. ' +
  'El cambio aplica a tu siguiente mensaje.';
